import { useCallback, useEffect, useRef } from 'react'
import isSafari from '../utils/isSafari'

const canBeScrolled = (element: HTMLElement): boolean => {
    return element.scrollHeight > element.clientHeight + 3 // Don't bother with a few pixels of difference as there are edge cases on iOS
}

export const useDisableScrollForModal = (
    condition: boolean,
    elementQueries: string[] = ['.v-modal-header', '.v-modal-footer', '.v-modal__background-overlay'],
    optionalIfNotScrollable = '.v-modal__body',
    allowedTargets: string[] = []
): void => {
    const preventScroll = useCallback(
        (e: TouchEvent) => {
            const target = e?.target as HTMLElement
            if (!!target && allowedTargets?.length && allowedTargets.some(query => target.matches(query))) {
                // Check if the allowed target can be scrolled, check parents which match the query too
                let element: HTMLElement | null = target
                do {
                    if (canBeScrolled(element)) {
                        return
                    }
                    element = element.parentElement
                } while (!!element && allowedTargets.some(query => element!.matches(query)))
            }

            e.preventDefault()
        },
        [allowedTargets]
    )

    useEffect(() => {
        if (!condition || !isSafari()) return

        elementQueries.forEach(query => {
            document.querySelector(query)?.addEventListener('touchmove', preventScroll, { passive: false })
        })

        return () => {
            elementQueries.forEach(query => {
                document.querySelector(query)?.removeEventListener('touchmove', preventScroll)
            })
        }
    }, [condition, elementQueries, preventScroll])

    // Modal body scroll check, it can change during the lifespan of the modal
    const blockModal = useRef<boolean>(false)
    useEffect(() => {
        if (!condition || !isSafari()) return
        blockModal.current = false

        const modalBody = optionalIfNotScrollable && (document.querySelector(optionalIfNotScrollable) as HTMLElement)
        if (!modalBody) return

        const blockFunction = () => {
            const blockModalNow = !canBeScrolled(modalBody)
            if (blockModal.current !== blockModalNow) {
                blockModal.current = blockModalNow
                if (blockModalNow) {
                    modalBody.addEventListener('touchmove', preventScroll, { passive: false })
                } else {
                    modalBody.removeEventListener('touchmove', preventScroll)
                }
            }
        }

        const mutationObserver = new MutationObserver(() => {
            blockFunction()
        })
        mutationObserver.observe(modalBody, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class'],
        })
        const resizeObserver = new ResizeObserver(() => {
            blockFunction()
        })
        resizeObserver.observe(modalBody)

        // Cleanup
        return () => {
            mutationObserver.disconnect()
            resizeObserver.disconnect()

            modalBody.removeEventListener('touchmove', preventScroll)
        }
    }, [condition, optionalIfNotScrollable, preventScroll])
}
